<template>
  <div class="flex h-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white/80 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
    <div class="border-b border-slate-200 px-4 py-4 dark:border-slate-700 sm:px-5 sm:py-5">
      <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {{ t('app.views.cashRegister.transactions') || 'Transactions' }}
      </h2>
    </div>
    
    <EmptyState
      v-if="transactions.length === 0"
      icon="folder"
      :title="t('app.views.cashRegister.noTransactions') || 'No transactions yet.'"
      variant="minimal"
      size="md"
    >
      <template #actions>
        <button
          class="inline-flex items-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          @click="emit('add-expense')"
        >
          {{ t('app.views.cashRegister.addExpense') || 'Add Expense' }}
        </button>
      </template>
    </EmptyState>
    
    <div
      v-else
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain lg:max-h-none divide-y divide-slate-200 dark:divide-slate-700/80 max-lg:overflow-visible"
    >
      <div 
        v-for="transaction in transactions" 
        :key="transaction.id" 
        class="px-4 py-4 transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/30 sm:px-5"
      >
        <div class="flex justify-between items-start gap-3">
          <div class="flex-1 min-w-0">
            <div class="mb-1 flex flex-wrap items-center gap-2">
              <p class="truncate text-base font-medium text-slate-900 dark:text-slate-100">
                {{ getDescription(transaction.description) }}
              </p>
              <span 
                v-if="transaction.payment_method" 
                class="rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap"
                :class="getPaymentMethodBadgeClass(transaction.payment_method)"
              >
                {{ getPaymentMethod(transaction.payment_method) }}
              </span>
            </div>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ formatDate(transaction.created_at) }}
            </p>
          </div>
          
          <div class="text-right flex-shrink-0">
            <p 
              class="whitespace-nowrap text-xl leading-none font-semibold"
              :class="transaction.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'"
            >
              {{ transaction.amount >= 0 ? '+' : '' }}{{ formatCurrency(Math.abs(transaction.amount) || 0) }}
            </p>
            <p class="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
              {{ getTransactionType(transaction.transaction_type) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/priceHelpers';
import EmptyState from '@/components/base/EmptyState.vue';
import {
  formatTransactionDate,
  translateDescription,
  translateTransactionType,
  translatePaymentMethod,
  getPaymentMethodBadgeClass,
  type Transaction
} from '@/utils/cashRegisterHelpers';

const { t } = useI18n();

const props = defineProps<{
  transactions: Transaction[];
}>();

const emit = defineEmits<{
  (e: 'add-expense'): void;
}>();

const formatDate = (dateString: string) => formatTransactionDate(dateString);
const getDescription = (description: string) => translateDescription(description, t);
const getTransactionType = (type: string) => translateTransactionType(type, t);
const getPaymentMethod = (method: string) => translatePaymentMethod(method, t);
</script>
